import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { clientsApi, servicesApi } from "../../../services";
import type { Client, FollowUpStatusItem } from "../../../types";
import { useCompany } from "../../../contexts/CompanyContext";
import { AdminPageHeader } from "../shared";
import { Icons } from "../../shared/Icons";
import { ClientsGridView } from "./ClientsGridView";
import { ClientsListView } from "./ClientsListView";
import { CreateClientModal } from "./CreateClientModal";
import { ViewMode, SearchBar, EmptyState, Card, ConfirmationModal } from "../../shared";
import { viewModeStorage } from "../../../utils/viewModeStorage";
import { indexFollowUpStatusByClientId } from "../AdminAppointments/CarExpiredFollowUpReminder";

export const AdminClients: React.FC = () => {
  const navigate = useNavigate();
  const { company } = useCompany();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    viewModeStorage.load("clients")
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [followUpByClientId, setFollowUpByClientId] = useState<
    Map<number, FollowUpStatusItem[]>
  >(() => new Map());

  const fetchFollowUpStatus = useCallback(async () => {
    try {
      const res = await servicesApi.getCompanyFollowUpStatus();
      if (res.success && res.data) {
        setFollowUpByClientId(indexFollowUpStatusByClientId(res.data.clients));
      } else {
        setFollowUpByClientId(new Map());
      }
    } catch {
      setFollowUpByClientId(new Map());
    }
  }, []);

  const fetchClients = useCallback(async () => {
    if (!company?.id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await clientsApi.getClientsByCompany();
      if (response.success && response.data && Array.isArray(response.data)) {
        setClients(response.data);
      } else {
        setClients([]); // Ensure clients is always an array
      }
    } catch (error) {
      setClients([]); // Ensure clients is always an array on error
      setError("Failed to fetch clients");
      console.error("Error fetching clients:", error);
    } finally {
      setLoading(false);
    }
  }, [company?.id]);

  useEffect(() => {
    if (company?.id) {
      fetchClients();
      fetchFollowUpStatus();
    }
  }, [company?.id, fetchClients, fetchFollowUpStatus]);

  const handleViewCars = (clientId: number) => {
    navigate(`/admin/clients/${clientId}/cars`);
  };

  const handleViewDetails = (clientId: number) => {
    navigate(`/admin/clients/${clientId}`);
  };

  const handleViewModeChange = (newViewMode: ViewMode) => {
    setViewMode(newViewMode);
    viewModeStorage.save("clients", newViewMode);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleCreateClient = () => {
    setEditingClient(null);
    setIsCreateModalOpen(true);
  };

  const handleEditClient = (client: Client) => {
    setEditingClient(client);
    setIsCreateModalOpen(true);
  };

  const handleDeleteClient = (clientId: number) => {
    setClientToDelete(clientId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!clientToDelete) return;

    setDeleteLoading(true);
    setError(null);

    try {
      const response = await clientsApi.deleteClient(clientToDelete);
      if (response.success) {
        await fetchClients(); // Refresh the list
        setShowDeleteModal(false);
        setClientToDelete(null);
      } else {
        setError(response.error || "Failed to delete client");
      }
    } catch (error) {
      console.error("Error deleting client:", error);
      setError("Failed to delete client");
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setClientToDelete(null);
  };

  const handleModalClose = () => {
    setIsCreateModalOpen(false);
    setEditingClient(null);
    fetchClients(); // Refresh the list
  };

  const filteredClients = Array.isArray(clients)
    ? clients.filter(
        (client) =>
          client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (client.phoneNumber ? client.phoneNumber.includes(searchQuery) : false) ||
          (client.cpf
            ? client.cpf.includes(searchQuery.replace(/\D/g, "")) ||
              client.cpf.includes(searchQuery)
            : false) ||
          (client.address
            ? client.address.toLowerCase().includes(searchQuery.toLowerCase())
            : false)
      )
    : [];

  if (loading) {
    return (
      <div className="space-y-6">
        <AdminPageHeader
          title="Clientes"
          subtitle="Gerencie seus clientes"
          icon={<Icons.Group sx={{ fontSize: 28 }} />}
        />
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Carregando clientes...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <AdminPageHeader
          title="Clientes"
          subtitle="Gerencie seus clientes"
          icon={<Icons.Group sx={{ fontSize: 28 }} />}
        />
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={fetchClients}
            className="mt-2 text-red-600 hover:text-red-800 font-medium"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <AdminPageHeader
        title="Clientes"
        subtitle={`${clients.length} cliente${
          clients.length !== 1 ? "s" : ""
        } cadastrado${clients.length !== 1 ? "s" : ""}`}
        icon={<Icons.Group sx={{ fontSize: 28 }} />}
        buttonIcon={<Icons.Add sx={{ fontSize: 20 }} />}
        buttonText="Novo Cliente"
        onButtonClick={handleCreateClient}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        showViewToggle={true}
        midleContent={
          <SearchBar
            value={searchQuery}
            onChange={handleSearch}
            placeholder="Buscar por nome, telefone ou endereço..."
            className="w-64"
          />
        }
      />

      {/* Content */}
      {loading ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 p-12 text-center">
          <div className="flex flex-col items-center gap-4">
            <Icons.Refresh sx={{ fontSize: 48 }} className="animate-spin text-gray-400" />
            <p className="text-xl font-medium text-gray-600">
              Carregando clientes...
            </p>
          </div>
        </div>
      ) : (
        <Card className="p-8">
          {filteredClients.length === 0 ? (
            <EmptyState
              icon={<Icons.Group sx={{ fontSize: 80 }} />}
              title={
                searchQuery
                  ? "Nenhum cliente encontrado"
                  : "Nenhum cliente cadastrado"
              }
              description={
                searchQuery
                  ? "Tente buscar com outros termos"
                  : "Comece adicionando seus primeiros clientes"
              }
              actionText={
                !searchQuery ? "Adicionar Primeiro Cliente" : undefined
              }
              onAction={!searchQuery ? handleCreateClient : undefined}
            />
          ) : (
            <>
              {viewMode === "grid" ? (
                <ClientsGridView
                  clients={filteredClients}
                  companyCategory={company?.category}
                  expiredByClientId={followUpByClientId}
                  onExpiredScheduled={fetchFollowUpStatus}
                  onEdit={handleEditClient}
                  onDelete={handleDeleteClient}
                  onViewCars={handleViewCars}
                  onViewDetails={handleViewDetails}
                />
              ) : (
                <ClientsListView
                  companyCategory={company?.category}
                  clients={filteredClients}
                  expiredByClientId={followUpByClientId}
                  onExpiredScheduled={fetchFollowUpStatus}
                  onEdit={handleEditClient}
                  onDelete={handleDeleteClient}
                  onViewCars={handleViewCars}
                  onViewDetails={handleViewDetails}
                />
              )}
            </>
          )}
        </Card>
      )}

      {/* Create/Edit Modal */}
      <CreateClientModal
        isOpen={isCreateModalOpen}
        onClose={handleModalClose}
        client={editingClient}
        companyId={company?.id || 0}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir Cliente"
        message="Tem certeza que deseja excluir este cliente? Esta ação não pode ser desfeita e todos os dados relacionados serão perdidos."
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        loading={deleteLoading}
        variant="danger"
      />
    </div>
  );
};
